import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useBreadcrumbStore } from '@ui/Layout/store/useBreadcrumbStore.ts';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader } from '@core-ui/Components/ui/card.tsx';
import { Integrations } from '@features/Settings/enums/IntegrationsEnums.ts';
import { Button } from '@core-ui/Components/ui/button.tsx';
import { IconStar } from '@tabler/icons-react';
import UseOutreachApi from '@features/Settings/hooks/useOutreachApi.ts';
import { getCurrentUser } from '@features/auth';
import { Avatar, AvatarFallback, AvatarImage } from '@core-ui/Components/ui/avatar.tsx';
import { Input } from '@core-ui/Components/ui/input.tsx';
import { Badge } from '@core-ui/Components/ui/badge.tsx';
import { OutreachMappingsComponent } from '@features/Settings/components/integrations/OutreachMappingsComponent.tsx';
import { type OutreachAccountView } from '@types';
import { z }  from 'zod';
import { UseOutreachMappingsApi } from '@features/Settings/hooks/useOutreachMappingsApi.ts';

export function OutreachPage (): React.JSX.Element {
  const { t } = useTranslation();
  const setItems = useBreadcrumbStore((s) => s.setItems);
  const currentUser = getCurrentUser();
  const { getAllOutreachAccounts, loginToOutreach } = UseOutreachApi();
  const { getOutreachMappingsByOrgId, getOutreachCustomDispositions, createOutreachDisposition, updateOutreachDisposition, deleteOutreachDisposition, patchOutreachMapping } = UseOutreachMappingsApi();
  
  const [outreachAccounts, setOutreachAccounts] = useState<Set<OutreachAccountView>>(new Set<OutreachAccountView>);
  const [isSearchingAccount, setIsSearchingAccount] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string | undefined>(undefined);
  
  const primaryAccount = useRef<OutreachAccountView>({
    id: '',
    nonce:'',
    email: '',
    isPrimary: false
  });
  const searchSchema = z.object({
    search: z.string().min(1, t('settings.integrationsPages.searchAccountError')).nullable()
  });

  const creationEnabled = useRef(false);
  const editionEnabled = useRef(false);
  const deletionEnabled = useRef(false);

  const validateSearch = useCallback((field: 'search', value: string) => {
    const partial = searchSchema.pick({ [field]: true });
    const result = partial.safeParse({ [field]: value });
    setValidationError(result.success ? undefined : result.error.errors[0]?.message);

  }, [searchSchema]);

  async function fetchAccounts (): Promise<OutreachAccountView[]> {
    setIsSearchingAccount(false);
    if (currentUser == null) {
      throw new Error('No current user found in session store.');
    }
    let accountList = [] as OutreachAccountView[];

    const accounts = await getAllOutreachAccounts(currentUser.userId, currentUser.organizationId);

    if (accounts.success && accounts.data !== undefined) {
      accountList = accounts.data.map((account) => {
        const { attributes: { email }, nonce, id } = account;
        return {
          id,
          email,
          nonce,
          isPrimary: false,
        };
      });
      setOutreachAccounts(new Set<OutreachAccountView>(accountList));
    }

    const primaryAccountObj = accountList.reduce((minAccount, currentAccount) => {
      return parseInt(currentAccount.id) < parseInt(minAccount.id) ? currentAccount : minAccount;
    });

    if (primaryAccountObj !== undefined) {
      primaryAccountObj.isPrimary = true;
      primaryAccount.current = primaryAccountObj;
    }
    return accountList;
  }


  function getUserInitials (name?: string): string {
    if (typeof name === 'string' && name.trim().length > 0) {
      return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w.charAt(0).toUpperCase())
        .join('');
    }
    return 'U';
  }

  async function handleLinkAccount (): Promise<void> {
    if (currentUser == null) {
      throw new Error('No current user found in session store.');
    }
    await loginToOutreach();
  }

  function handleAccountSearch (event: React.FormEvent<HTMLInputElement>): void {

    let search = '';
    if (event.target !== null) {
      search = (event.target as HTMLInputElement).value;
      validateSearch('search', search);
    }

    const filteredAccounts = Array.from(outreachAccounts).filter((account) =>
      account.email.toLowerCase().includes(search.toLowerCase())
    );
    setIsSearchingAccount(search !== '');

    setOutreachAccounts(new Set<OutreachAccountView>(filteredAccounts));
  }

  function setUserPermissions (): void {
    if (currentUser !== null) {
      creationEnabled.current = currentUser.isSuperAdmin || currentUser.isAdmin;
      editionEnabled.current = currentUser.isSuperAdmin || currentUser.isAdmin;
      deletionEnabled.current = currentUser.isSuperAdmin || currentUser.isAdmin;
    }
  }

  useEffect (()=> {
    setUserPermissions();
  }, [currentUser]);

  useEffect(() => {
    if (!isSearchingAccount) {
      void fetchAccounts();
    }
  }, [isSearchingAccount]);

  useEffect(() => {
    setItems([
      { labelKey: 'breadcrumb.home', href: '/workspace/sessions/new' },
      { labelKey: 'breadcrumb.settings', href: '/workspace/settings/:activeTab' },  // This ensures the account tab is rendered by default.
      { labelKey: 'breadcrumb.app', href: '/workspace/settings/app' },
      { labelKey: 'breadcrumb.outreach', href: '/workspace/settings/app/outreach' },
    ]);
  }, [setItems]);

  return (
    <div className="grid gap-4">
      <header id="page-title" className="flex gap-3 items-center">
        <h1 className='text-3xl font-semibold'>{t('settings.integrationsPages.pageTitle', { integrationName: Integrations.Outreach })}</h1>
        <Badge
          className="flex h-2 text-xs p-2 items-center rounded-sm font-semibold bg-[var(--success-badge)] text-[var(--success-text)] border border-[var(--success-badge-border)]">
          {t('settings.appTab.cardStatus.connected')}
        </Badge>
      </header>
      <h2>{t('settings.integrationsPages.pageSubtitle')}</h2>

      <Card className="p-2 rounded-2xl shadow-lg border border-border bg-card text-foreground mt-4">
        <CardContent className="grid">
          <CardHeader className="items-start w-full px-0 mb-4">
            <header id="accounts-headline" className="flex gap-3 items-center">
              <h2 className='text-xl font-semibold'>{t('settings.integrationsPages.linkAccountsTitle')}</h2>
              <Badge
                className="flex h-2 text-xs p-2 items-center rounded-sm font-semibold bg-[var(--success-badge)] text-[var(--success-text)] border border-[var(--success-badge-border)]">
                {t('settings.appTab.cardStatus.connected')}
              </Badge>
            </header>
            <h3 className="text-sm">{t('settings.integrationsPages.linkAccountsSubtitle', { integrationName: Integrations.Outreach })}</h3>
            <section className="flex w-full justify-between items-center my-4 gap-2">
              <div className="grid w-full"> <Input
                className={`w-2/3 lg:w-2/5 placeholder:text-gray-500 text-foreground border w-full focus:outline-none focus:ring-1 ${
                  validationError !== undefined
                    ? '!border-error focus:!ring-error'
                    : 'border-input focus:ring-ring'
                }`}    
                placeholder={t('settings.integrationsPages.searchAccountPlaceholder')}
                onInput={handleAccountSearch}
              />
              {validationError != null && (
                <p className="text-xs text-error mt-1" data-qa-id="intent-title-error">{validationError}</p>
              )}
              </div>
              <Button onClick={() => {
                void handleLinkAccount();
              }}>
                {t('settings.integrationsPages.addAccount')}
              </Button>
            </section>
            <section className="text-sm">
              <h4>{t('settings.integrationsPages.linkedAccountsOutreach', {
                number: outreachAccounts.size,
                plural: outreachAccounts.size > 1 ? 's' : ''
              })
              }
              <b>{primaryAccount.current.email}</b>
              </h4>
            </section>
          </CardHeader>
          <main id="accounts-listing" className="grid max-h-[35vh] overflow-y-auto">
            {Array.from(outreachAccounts).map((account, index) => {
              return (
                <div
                  key={`user-in-account-${index}`}
                  className="flex rounded-sm border border-border text-foreground px-2 py-2">
                  <div id="account-data-container" className="flex items-center w-full">
                    <div className="flex gap-4 items-center w-full">
                      <Avatar className="flex w-8 h-8 justify-center items-center">
                        <AvatarImage
                          src={account.avatarSrc?.toString()}
                          alt={account.email}
                          className="bg-primary-foreground dark:bg-background w-8 h-8"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                        <AvatarFallback
                          className="flex items-center justify-center h-8 w-8 text-foreground/90 text-md font-medium bg-muted">
                          {getUserInitials(account.email)}
                        </AvatarFallback>
                      </Avatar>

                      <div>
                        <p className="text-sm font-semibold overflow-hidden text-ellipsis">{account.email}</p>

                      </div>

                      <Badge
                        className="flex h-2 text-xs p-2 items-center rounded-sm font-semibold bg-[var(--success-badge)] text-[var(--success-text)] border border-[var(--success-badge-border)]">
                        {t('settings.appTab.cardStatus.connected')}
                      </Badge>
                      {account.email === primaryAccount.current.email &&
                              <Badge
                                className="flex h-2 text-xs p-2 items-center rounded-lg font-semibold border border-border bg-gray-200/30 text-muted-foreground ">
                                {t('settings.outreachPage.primaryLabel')}
                              </Badge>
                      }
                    </div>
                  </div>
                  <div id="account-actions-box" className=" flex gap-1 place-self-center">
                    <IconStar className="w-4 h-4 m-0 text-primary hover:text-muted-foreground"></IconStar>
                  </div>
                </div>
              );
            })}
          </main>
        </CardContent>
      </Card>

      <Card className="p-2 rounded-2xl shadow-lg border border-border bg-card text-foreground mt-4">
        <CardContent className="grid overflow-y-auto">
          <CardHeader className="items-start h-full w-full p-4 mb-1">
            <header id="mappings-headline" className="grid gap-3 items-center">
              <h2 className='text-xl font-semibold'>{t('settings.integrationsPages.mappings.mappingsTitle')}</h2>
              <h3 className="text-sm">{t('settings.integrationsPages.mappings.mappingsSubtitle')}</h3>
            </header>
            <OutreachMappingsComponent
              fetchAccountsCallback={getAllOutreachAccounts} 
              fetchMappingsCallback={getOutreachMappingsByOrgId} 
              getDispositionsCallback={getOutreachCustomDispositions}
              createDispositionCallback={createOutreachDisposition}
              updateDispositionCallback={updateOutreachDisposition}
              deleteDispositionCallback={deleteOutreachDisposition}
              patchMappingCallback={patchOutreachMapping}
              editionEnabled={editionEnabled.current}
              creationEnabled={creationEnabled.current}
              deletionEnabled={deletionEnabled.current}
            />
          </CardHeader>
        </CardContent>
      </Card>
    </div>
  );
}